import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Rnd } from 'react-rnd';
import Barcode from 'react-barcode';
import html2canvas from 'html2canvas';
import { apiFetch } from '../../utils/api';
import { Store } from 'react-notifications-component';
import 'react-notifications-component/dist/theme.css';

const pxToDots = (px) => Math.round(px * 1.6);
const dotsToPx = (dots) => Math.round(dots / 1.6);

const BarcodePrinter = () => {
  const [searchParams] = useSearchParams();

  const [sizeMode, setSizeMode] = useState('free');
  const [tagW, setTagW] = useState(60);
  const [tagH, setTagH] = useState(92);
  const [paperType, setPaperType] = useState('0T');
  const [controlPoint, setControlPoint] = useState(0);

  const [elements, setElements] = useState([]);
  const [printCount, setPrintCount] = useState(1);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [activeDragId, setActiveDragId] = useState(null);
  const [activeElementId, setActiveElementId] = useState(null);

  const [tempName, setTempName] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const pinkHeaderHeight = 40;
  const whiteCanvasHeight = tagH * 5 - pinkHeaderHeight;

  const showNotification = (type, title, message) => {
    Store.addNotification({
      title, message, type,
      insert: "top", container: "top-right",
      animationIn: ["animated", "fadeIn"], animationOut: ["animated", "fadeOut"],
      dismiss: { duration: 3000, onScreen: true }, dismissable: { click: true }
    });
  };

  useEffect(() => {
    const uuid = searchParams.get('uuid');
    if (uuid) loadTemplate(uuid);
  }, [searchParams]);

  const loadTemplate = async (uuid) => {
    setIsLoading(true);
    try {
      const response = await apiFetch(`/barcode/templates/${uuid}`);
      if (!response.ok) throw new Error('読み込み失敗');

      const data = await response.json();
      const td = data.temp_data;

      setTempName(data.name);
      setTagW(td.tagW || 60);
      setTagH(td.tagH || 92);
      setPaperType(td.paperType || '0T');
      setControlPoint(td.controlPoint || 0);

      if (td.tagW === 37 && td.tagH === 60) setSizeMode('37x60');
      else if (td.tagW === 46 && td.tagH === 108) setSizeMode('46x108');
      else setSizeMode('free');

      setElements(td.elements || []);
      showNotification('info', '読込完了', `「${data.name}」を読み込みました`);
    } catch (error) {
      showNotification('danger', 'エラー', '読み込みに失敗しました。');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!tempName.trim()) {
      showNotification('warning', '確認', 'テンプレート名を入力してください。');
      return;
    }

    try {
      const checkRes = await apiFetch(`/barcode/templates/check?name=${encodeURIComponent(tempName)}`);
      const checkData = await checkRes.json();

      if (checkData.error === 'value_already_in_database_or_not_empty') {
        if (!window.confirm(`「${tempName}」は存在します。上書きしますか？`)) return;
      }

      const payload = {
        name: tempName,
        temp_data: { tagW, tagH, paperType, controlPoint, elements }
      };

      const saveRes = await apiFetch(`/barcode/templates`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      if (!saveRes.ok) throw new Error('保存失敗');
      const saveData = await saveRes.json();
      showNotification('info', '保存完了', saveData.message);
      setShowModal(false);
    } catch (error) {
      showNotification('danger', 'エラー', '保存に失敗しました。');
    }
  };

  const handleSizeModeChange = (mode) => {
    setSizeMode(mode);
    if (mode === '37x60') { setTagW(37); setTagH(60); }
    else if (mode === '46x108') { setTagW(46); setTagH(108); }
  };

  const addElement = (type) => {
    const newId = Date.now().toString();
    const newElement = {
      id: newId, type,
      content: type === 'barcode' ? '1234567890128' : type === 'price' ? '1990' : type === 'border' ? '200' : type === 'text-eisuu' ? 'DESINE No.' : 'Twinkle Girls',
      format: type === 'barcode' ? 'EAN13' : type === 'price' ? 'X73' : type === 'border' ? 'FW02' : undefined,
      size: (type === 'text' || type === 'text-eisuu') ? '16' : undefined,
      x: 0, y: 0,
    };
    setElements([...elements, newElement]);
    setActiveElementId(newId);
  };

  const updateElement = (id, key, value) => {
    setElements(prev => prev.map(el => el.id === id ? { ...el, [key]: value } : el));
  };

  const removeElement = (id) => {
    setElements(prev => prev.filter(el => el.id !== id));
    if (activeElementId === id) setActiveElementId(null);
  };

  const centerElement = (id) => {
    const elDom = document.getElementById(`preview-${id}`);
    if (elDom) {
      const elWidth = elDom.offsetWidth;
      const canvasWidth = tagW * 5;
      const centerX = Math.floor((canvasWidth - elWidth) / 2);
      updateElement(id, 'x', Math.max(0, pxToDots(centerX)));
    }
  };

  const centerAllElements = () => elements.forEach(el => centerElement(el.id));

  const getTypeName = (type) => {
    switch (type) {
      case 'text': return 'テキスト'; case 'text-eisuu': return '半角英数';
      case 'barcode': return 'バーコード'; case 'price': return '価格';
      case 'border': return '罫線'; default: return type;
    }
  };

  const isValidBarcode = (format, content) => {
    if (!/^\d+$/.test(content)) return false;
    return format === 'EAN8' ? (content.length === 7 || content.length === 8) : (content.length === 12 || content.length === 13);
  };

  const handleDragStart = (e, index) => { setDraggedIndex(index); e.dataTransfer.effectAllowed = "move"; };
  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    const newElements = [...elements];
    const draggedItem = newElements[draggedIndex];
    newElements.splice(draggedIndex, 1);
    newElements.splice(index, 0, draggedItem);
    setElements(newElements);
    setDraggedIndex(index);
  };
  const handleDragEnd = () => { setDraggedIndex(null); setActiveDragId(null); };

  const handlePrint = async () => {
    if (elements.length === 0) {
      showNotification('warning', '確認', '印刷する要素がありません！');
      return;
    }

    // キャプチャ時に青枠が写り込むのを防ぐ
    setActiveElementId(null);
    const canvasElement = document.getElementById('print-canvas');
    if (!canvasElement) return;

    try {
      // 一時的に枠線を消去
      const originalBorder = canvasElement.style.border;
      canvasElement.style.border = 'none';
      // フォントの読み込み完了を待機
      await document.fonts.ready;
      const canvas = await html2canvas(canvasElement, { scale: 1.6, backgroundColor: '#FFFFFF', useCORS: true });
      canvasElement.style.border = originalBorder;

      const payload = {
        image_base64: canvas.toDataURL('image/png'),
        tag_w: tagW, tag_h: tagH,
        paper_type: paperType, print_count: printCount, control_point: controlPoint
      };

      const response = await apiFetch(`/barcode/tagprint`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      showNotification('info', '送信完了', '印刷データを送信しました！');
    } catch (error) {
      showNotification('danger', 'エラー', '通信または画像処理に失敗しました。');
    }
  };

  if (isLoading) return <div className="text-center py-5">読み込み中...</div>;

  return (
    <div>
      <style>{`@import url('https://fonts.googleapis.com/css?family=Timmana');`}</style>
      <h1 className="page-header">Print <small>フリー印刷</small></h1>

      <div className="row">
        <div className="col-md-7">
          <div className="panel panel-inverse">
            <div className="panel-heading"><h4 className="panel-title">フリー印刷設定</h4></div>
            <div className="panel-body">
              <div className="row mb-3">
                <div className="col-12 mb-2 d-flex justify-content-end">
                  <button className="btn btn-info btn-sm" onClick={() => setShowModal(true)}>
                    <i className="fas fa-save me-1"></i> テンプレート保存
                  </button>
                </div>
                <div className="col-xl-12 mb-2 d-flex flex-wrap gap-2">
                  <div className="btn-group">
                    <input type="radio" className="btn-check" id="paper3760" checked={sizeMode === '37x60'} onChange={() => handleSizeModeChange('37x60')} />
                    <label className="btn btn-default" htmlFor="paper3760">37×60mm</label>
                    <input type="radio" className="btn-check" id="paper46108" checked={sizeMode === '46x108'} onChange={() => handleSizeModeChange('46x108')} />
                    <label className="btn btn-default" htmlFor="paper46108">46×108mm</label>
                    <input type="radio" className="btn-check" id="paperfree" checked={sizeMode === 'free'} onChange={() => handleSizeModeChange('free')} />
                    <label className="btn btn-default" htmlFor="paperfree">自由変形</label>
                  </div>
                  <div className="btn-group">
                    <input type="radio" className="btn-check" id="typeTag" checked={paperType === '0T'} onChange={() => setPaperType('0T')} />
                    <label className="btn btn-white" htmlFor="typeTag">タグ</label>
                    <input type="radio" className="btn-check" id="typeLabel" checked={paperType === '0L'} onChange={() => setPaperType('0L')} />
                    <label className="btn btn-white" htmlFor="typeLabel">ラベル</label>
                  </div>
                  <div className="input-group w-auto">
                    <span className="input-group-text">基準点</span>
                    <input type="number" className="form-control" style={{ maxWidth: '80px' }} value={controlPoint} onChange={(e) => setControlPoint(parseInt(e.target.value) || 0)} />
                  </div>
                </div>
                <div className="col-12 d-flex align-items-center gap-3">
                  <div className="d-flex align-items-center">
                    <label className="me-2 text-nowrap">横幅(mm)：</label>
                    <input type="number" className="form-control" style={{ width: '90px' }} min="32" max="80" value={tagW} disabled={sizeMode !== 'free'} onChange={(e) => setTagW(parseInt(e.target.value) || 32)} />
                  </div>
                  <div className="d-flex align-items-center">
                    <label className="me-2 text-nowrap">縦幅(mm)：</label>
                    <input type="number" className="form-control" style={{ width: '90px' }} min="25" max="181" value={tagH} disabled={sizeMode !== 'free'} onChange={(e) => setTagH(parseInt(e.target.value) || 25)} />
                  </div>
                </div>
              </div>
              <hr />

              <div className="btn-group mb-3 w-100 d-flex flex-wrap gap-1">
                <button className="btn btn-default flex-grow-1" onClick={() => addElement('text')}>テキスト</button>
                <button className="btn btn-default flex-grow-1" onClick={() => addElement('text-eisuu')}>半角英数</button>
                <button className="btn btn-default flex-grow-1" onClick={() => addElement('barcode')}>バーコード</button>
                <button className="btn btn-default flex-grow-1" onClick={() => addElement('price')}>価格</button>
                <button className="btn btn-default flex-grow-1" onClick={() => addElement('border')}>罫線</button>
                <button className="btn btn-secondary flex-grow-1" onClick={centerAllElements}><i className="fas fa-align-center me-1"></i> 中央</button>
              </div>

              <div className="table-responsive">
                <table className="table table-bordered align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '5%' }}></th><th style={{ width: '18%' }}>タイプ</th><th>表示内容</th>
                      <th style={{ width: '22%' }}>X座標</th><th style={{ width: '12%' }}>Y座標</th><th style={{ width: '10%' }}>削除</th>
                    </tr>
                  </thead>
                  <tbody>
                    {elements.map((el, index) => (
                      <tr key={el.id} draggable={activeDragId === el.id} onDragStart={(e) => handleDragStart(e, index)} onDragOver={(e) => handleDragOver(e, index)} onDragEnd={handleDragEnd} onClick={() => setActiveElementId(el.id)} style={{ backgroundColor: activeElementId === el.id ? '#e7f1ff' : (draggedIndex === index ? '#f8f9fa' : 'transparent'), opacity: draggedIndex === index ? 0.5 : 1, transition: 'all 0.2s ease' }}>
                        <td className="text-center text-muted" style={{ cursor: 'grab' }} onMouseDown={() => setActiveDragId(el.id)} onMouseUp={() => setActiveDragId(null)} onMouseLeave={() => setActiveDragId(null)}><i className="fas fa-grip-vertical"></i></td>
                        <td>
                          <div className="fw-bold mb-1">{getTypeName(el.type)}</div>
                          {el.type === 'barcode' && <select className="form-select form-select-sm" value={el.format || 'EAN13'} onChange={(e) => updateElement(el.id, 'format', e.target.value)}><option value="EAN13">JAN-13</option><option value="EAN8">JAN-8</option></select>}
                          {el.type === 'price' && <select className="form-select form-select-sm" value={el.format || 'X73'} onChange={(e) => updateElement(el.id, 'format', e.target.value)}><option value="X70">X70</option><option value="X71">X71</option><option value="X72">X72</option><option value="X73">X73</option></select>}
                          {el.type === 'border' && <select className="form-select form-select-sm" value={el.format || 'FW02'} onChange={(e) => updateElement(el.id, 'format', e.target.value)}><option value="FW02">2dot</option><option value="FW04">4dot</option></select>}
                          {(el.type === 'text' || el.type === 'text-eisuu') && <select className="form-select form-select-sm" value={el.size || '16'} onChange={(e) => updateElement(el.id, 'size', e.target.value)}><option value="16">16dot</option><option value="24">24dot</option></select>}
                        </td>
                        <td>
                          {el.type === 'border' ? (
                            <div className="input-group input-group-sm"><input type="number" className="form-control" value={el.content} onChange={(e) => updateElement(el.id, 'content', e.target.value)} /><span className="input-group-text">dots</span></div>
                          ) : (
                            <input type="text" className="form-control form-control-sm" value={el.content} onChange={(e) => updateElement(el.id, 'content', el.type === 'text-eisuu' ? e.target.value.replace(/[^ -~]/g, '') : e.target.value)} />
                          )}
                        </td>
                        <td>
                          <div className="input-group input-group-sm">
                            <input type="number" className="form-control" value={el.x} onChange={(e) => updateElement(el.id, 'x', parseInt(e.target.value) || 0)} />
                            <button className="btn btn-outline-secondary px-2" onClick={() => centerElement(el.id)}><i className="fas fa-align-center"></i></button>
                          </div>
                        </td>
                        <td><input type="number" className="form-control form-control-sm" value={el.y} onChange={(e) => updateElement(el.id, 'y', parseInt(e.target.value) || 0)} /></td>
                        <td><button className="btn btn-danger btn-sm w-100" onClick={(e) => { e.stopPropagation(); removeElement(el.id); }}><i className="fas fa-trash-alt"></i></button></td>
                      </tr>
                    ))}
                    {elements.length === 0 && <tr><td colSpan="6" className="text-center text-muted">要素がありません。</td></tr>}
                  </tbody>
                </table>
              </div>
              <div className="d-flex align-items-center mt-3">
                <label className="me-2 fw-bold text-nowrap">印刷枚数：</label>
                <input type="number" className="form-control w-25 me-3" min="1" value={printCount} onChange={(e) => setPrintCount(parseInt(e.target.value) || 1)} />
                <button className="btn btn-primary flex-grow-1" onClick={handlePrint}><i className="fas fa-print me-2"></i> 発行する</button>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-5">
          <div className="panel panel-inverse">
            <div className="panel-heading"><h4 className="panel-title">プレビュー（ドラッグ可能）</h4></div>
            <div className="panel-body bg-light" style={{ minHeight: '600px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <div style={{ width: tagW * 5, height: tagH * 5, display: 'flex', flexDirection: 'column', boxShadow: '0 4px 8px rgba(0,0,0,0.1)', borderRadius: '8px' }}>
                <div style={{ height: pinkHeaderHeight, backgroundColor: '#FF9985', borderTopLeftRadius: '8px', borderTopRightRadius: '8px', border: '1px solid #ccc', borderBottom: '1px dashed #fff' }}></div>
                <div id="print-canvas" style={{ height: whiteCanvasHeight, backgroundColor: 'white', border: '1px solid #ccc', borderTop: 'none', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px', position: 'relative', overflow: 'hidden' }} onClick={() => setActiveElementId(null)}>
                  {elements.map((el) => {
                    const pxX = dotsToPx(el.x); const pxY = dotsToPx(el.y);
                    const isOverflow = pxX < 0 || pxY < 0 || pxX > (tagW * 5 - 20) || pxY > (whiteCanvasHeight - 20);
                    const priceFontSize = el.format === 'X70' ? '32px' : el.format === 'X71' ? '40px' : el.format === 'X72' ? '48px' : '70px';
                    const textFontSize = el.size === '24' ? '16px' : '10px';
                    const letterSpacing = el.type === 'price' ? '12px' : (el.type.includes('text') && el.size === '24') ? '2px' : 'normal';

                    return (
                      <Rnd key={el.id} id={`preview-${el.id}`} position={{ x: pxX, y: pxY }} size={el.type === 'border' ? { width: dotsToPx(parseInt(el.content) || 200), height: 12 } : undefined} enableResizing={el.type === 'border' ? { left: true, right: true } : false} onDragStart={(e) => { e.stopPropagation(); setActiveElementId(el.id); }} onClick={(e) => { e.stopPropagation(); setActiveElementId(el.id); }} onDragStop={(e, data) => { updateElement(el.id, 'x', pxToDots(data.x)); updateElement(el.id, 'y', pxToDots(data.y)); }} onResizeStop={(e, direction, ref, delta, position) => { if (el.type === 'border') { updateElement(el.id, 'content', String(pxToDots(ref.offsetWidth))); updateElement(el.id, 'x', pxToDots(position.x)); } }} bounds="parent" style={{ outline: activeElementId === el.id ? '1px solid #0d6efd' : '1px dashed transparent', zIndex: activeElementId === el.id ? 20 : 10, backgroundColor: isOverflow ? 'rgba(242, 142, 152, 0.7)' : 'transparent', transition: 'background-color 0.2s ease' }} className="hover-border">
                        {(el.type === 'text' || el.type === 'text-eisuu') && <span style={{ fontSize: textFontSize, fontFamily: '"MgenPlus", sans-serif', letterSpacing: letterSpacing, fontWeight: 'bold', userSelect: 'none', whiteSpace: 'nowrap' }}>{el.content}</span>}
                        {el.type === 'price' && <span style={{ fontSize: priceFontSize, fontFamily: "'Timmana', sans-serif", letterSpacing: letterSpacing, userSelect: 'none', whiteSpace: 'nowrap' }}>{el.content}</span>}
                        {el.type === 'border' && <div style={{ width: '100%', height: el.format === 'FW04' ? '4px' : '2px', backgroundColor: 'black', position: 'absolute', top: 0, left: 0 }}></div>}
                        {el.type === 'barcode' && <div style={{ pointerEvents: 'none' }}>{isValidBarcode(el.format, el.content) ? <Barcode value={el.content} format={el.format || "EAN13"} width={1.25} height={38} fontSize={12} margin={0} displayValue={false} flat={true} /> : <div style={{ width: '120px', height: '38px', backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: '12px' }}>無効な桁数</div>}</div>}
                      </Rnd>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header"><h5 className="modal-title">テンプレート保存</h5><button type="button" className="btn-close" onClick={() => setShowModal(false)}></button></div>
              <div className="modal-body"><div className="form-group"><label className="form-label">テンプレート名</label><input type="text" className="form-control" value={tempName} onChange={(e) => setTempName(e.target.value)} autoFocus /></div></div>
              <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>キャンセル</button><button type="button" className="btn btn-primary" onClick={handleSaveTemplate}>保存</button></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BarcodePrinter;